import React, { useCallback, useMemo } from 'react'

import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
} from '@tanstack/react-table'

import { Tooltip, classnames } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import {
  arrayOf, bool, func, node, object, oneOf, shape, string,
} from 'prop-types'

import Icon from 'Pods/Shared/Icon/Icon'

import { EMPTY_ARRAY } from 'Utils/stableDefaults'

// Convert the simple `columns` prop into tanstack's ColumnDef format.
// Consumers never see the internals — they pass a flat array like:
//
//   [
//     { key: 'date', header: 'Date' },
//     { key: 'price', header: 'Price', cell: ({ value }) => `$${value}` },
//     { key: 'total', header: 'Total', accessor: (row) => row.a + row.b },
//     { key: 'actions', header: 'Actions', virtual: true, cell: () => <Button/> },
//   ]
function toColumnDefs(columns) {
  return columns
    .filter((column) => column.visible !== false)
    .map((column) => {
      const def = {
        id: column.key,
        header: column.header,
        enableSorting: !!column.sortable,
        meta: {
          align: column.align,
          width: column.width,
          minWidth: column.minWidth,
          tooltip: column.tooltip,
          sortable: column.sortable,
        },
      }

      if (column.accessor) {
        def.accessorFn = column.accessor
      } else if (!column.virtual) {
        def.accessorKey = column.key
      }

      if (column.cell) {
        def.cell = ({ getValue, row }) => column.cell({
          value: getValue(),
          row: row.original,
        })
      }

      return def
    })
}

function getSortIcon(columnKey, sortKey, sortDirection) {
  if (columnKey !== sortKey) return 'sort'
  return sortDirection === 'asc' ? 'caret-up' : 'caret-down'
}

function getNextSortDirection(columnKey, sortKey, sortDirection) {
  if (columnKey !== sortKey) return 'asc'
  if (sortDirection === 'asc') return 'desc'
  return null
}

function getThClassName(align, tooltip, sortable, isActive) {
  return classnames(
    `cgt-legacy-text--align-${align || 'left'}`,
    tooltip && 'has-tooltip',
    sortable && 'data-table__th--sortable',
    isActive && 'data-table__th--active'
  )
}

function filterValidRows(data) {
  return (data || []).filter((row) => row && typeof row === 'object')
}

function DataTable({
  columns = EMPTY_ARRAY,
  data = EMPTY_ARRAY,
  className = '',
  emptyMessage = null,
  sortKey = null,
  sortDirection = null,
  onSort = null,
  manualSorting = false,
}) {
  const validRows = useMemo(() => filterValidRows(data), [data])
  const columnDefs = useMemo(() => toColumnDefs(columns), [columns])

  const sortingState = useMemo(
    () => (sortKey ? [{ id: sortKey, desc: sortDirection === 'desc' }] : []),
    [sortKey, sortDirection]
  )

  const handleHeaderClick = useCallback((columnKey, sortable) => {
    if (!sortable || !onSort) return
    const nextDirection = getNextSortDirection(columnKey, sortKey, sortDirection)
    onSort(columnKey, nextDirection)
  }, [onSort, sortKey, sortDirection])

  const handleHeaderKeyDown = useCallback((event, columnKey, sortable) => {
    if (event.key !== 'Enter' && event.key !== ' ') return
    event.preventDefault()
    handleHeaderClick(columnKey, sortable)
  }, [handleHeaderClick])

  const table = useReactTable({
    data: validRows,
    columns: columnDefs,
    state: { sorting: sortingState },
    manualSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  if (!validRows.length) {
    return emptyMessage
  }

  const headerGroup = table.getHeaderGroups()[0]

  return (
    <div className={`data-table ${className}`}>
      <table>
        <colgroup>
          {columnDefs.map((col) => {
            const width = col.meta?.width
            return (
              <col
                key={col.id}
                style={{
                  width: width || 'auto',
                  ...(width && width !== 'auto' && {
                    minWidth: width,
                    maxWidth: width,
                  }),
                }}
              />
            )
          })}
        </colgroup>

        <thead>
          <tr>
            {headerGroup.headers.map((header) => {
              const { column } = header
              const meta = column.columnDef.meta || {}
              const {
                tooltip, align, width, minWidth, sortable,
              } = meta
              const isActive = sortable && column.id === sortKey
              const thClassName = getThClassName(align, tooltip, sortable, isActive)

              const titleContent = flexRender(
                column.columnDef.header,
                header.getContext()
              )

              const wrappedTitle = tooltip ? (
                <Tooltip
                  id={`tooltip-${column.id}`}
                  randomizeID
                  content={tooltip}
                  clickable
                  place="top"
                >
                  <span className="tooltip-trigger">{titleContent}</span>
                </Tooltip>
              ) : titleContent

              return (
                <th
                  key={header.id}
                  className={thClassName}
                  style={{
                    ...(minWidth && { minWidth }),
                    ...(width && { width }),
                  }}
                  onClick={() => handleHeaderClick(column.id, sortable)}
                  role={sortable ? 'button' : undefined}
                  tabIndex={sortable ? 0 : undefined}
                  onKeyDown={sortable
                    ? (event) => handleHeaderKeyDown(event, column.id, sortable)
                    : undefined}
                >
                  {wrappedTitle}
                  {sortable && (
                    <Icon
                      type={['fas', getSortIcon(column.id, sortKey, sortDirection)]}
                      className="data-table__sort-icon"
                      size="sm"
                    />
                  )}
                </th>
              )
            })}
          </tr>
        </thead>

        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr key={row.id}>
              {row.getVisibleCells().map((cell) => {
                const cellRenderer = cell.column.columnDef.cell
                const context = cell.getContext()
                return (
                  <td key={cell.id}>
                    {typeof cellRenderer === 'function'
                      ? cellRenderer(context)
                      : flexRender(cellRenderer, context)}
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

DataTable.propTypes = {
  columns: arrayOf(shape({
    key: string.isRequired,
    header: node,
    cell: func,
    accessor: func,
    visible: bool,
    virtual: bool,
    sortable: bool,
    width: string,
    minWidth: string,
    align: oneOf(['left', 'center', 'right']),
    tooltip: node,
  })).isRequired,
  data: arrayOf(object),
  className: string,
  emptyMessage: node,
  sortKey: string,
  sortDirection: oneOf(['asc', 'desc']),
  onSort: func,
  manualSorting: bool,
}

export default DataTable
